/**
 * Client-side image compression using Canvas API.
 * Used as a fallback when original-quality upload struggles on weak connections.
 */

/**
 * Compress an image File/Blob to a target max dimension and quality.
 *
 * @param {File|Blob} file - The original image
 * @param {Object} options
 * @param {number} options.maxWidth - Max width in px (default 2048)
 * @param {number} options.quality - JPEG quality 0-1 (default 0.8)
 * @returns {Promise<Blob>} compressed JPEG blob
 */
export async function compressImage(file, { maxWidth = 2048, quality = 0.8 } = {}) {
  // Only compress images — pass through videos
  if (!file.type || !file.type.startsWith("image/")) return file;

  const bitmap = await loadBitmap(file);
  const scale = Math.min(1, maxWidth / bitmap.width);
  const targetW = Math.round(bitmap.width * scale);
  const targetH = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, targetW, targetH);

  const blob = await canvasToBlob(canvas, quality);
  // Preserve original filename with .jpg extension for clarity
  const originalName = file.name || "photo.jpg";
  const newName = originalName.replace(/\.[^.]+$/, "") + ".jpg";
  return new File([blob], newName, { type: "image/jpeg" });
}

function loadBitmap(file) {
  // createImageBitmap handles HEIC on some browsers, falls back to Image otherwise
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file).catch(() => loadImageElement(file));
  }
  return loadImageElement(file);
}

function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      "image/jpeg",
      quality
    );
  });
}
